import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../Redux/authSlice";
import { orgApi } from "../Redux/orgApi";
import { useUsage } from "./UsageContext";

const NotificationsContext = createContext(null);

// Used by the focus/visibility safety-net handlers to avoid hammering the
// API. Real-time updates now flow through the SSE stream; polling is gone.
const MIN_REFETCH_GAP_MS = 5_000;

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "http://localhost:5050").replace(/\/$/, "");

export const NotificationsProvider = ({ children }) => {
  // Auth gate: skip all notification work when no user is logged in.
  // This stops the SSE endpoint from being called on /login, public pages,
  // or after logout — which would otherwise produce a 401 retry loop.
  const user = useSelector(selectUser);
  const isLoggedIn = !!user;
  const dispatch = useDispatch();
  // The plan (Pro / Free) changes with the team seat, so it refreshes too.
  const { refresh: refreshUsage } = useUsage();

  // Everything that depends on the user's team: membership (sidebar, team
  // pages, Manage Subscription), the Team page data, team billing, invites,
  // the personal subscription, and the plan / minutes. Refetched at once.
  // `force: false` keeps the usual 30 s gap for the minutes (tab switches).
  const syncTeamState = useCallback(
    ({ force = true } = {}) => {
      dispatch(orgApi.util.invalidateTags(["OrgMe", "Org", "OrgBilling", "OrgInvites", "Subscription"]));
      refreshUsage({ force });
    },
    [dispatch, refreshUsage]
  );

  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Refs prevent stale-closure issues and let us suppress hammering the API.
  const inFlightRef = useRef(null);
  // A forced fetch requested while one was running; runs when it finishes so
  // a notification created mid-request isn't missed.
  const refetchQueuedRef = useRef(false);
  const lastFetchAtRef = useRef(0);
  const mountedRef = useRef(true);

  const fetchNotifications = useCallback(async ({ force = false } = {}) => {
    if (inFlightRef.current) {
      if (force) refetchQueuedRef.current = true;
      return inFlightRef.current;
    }
    if (!force && Date.now() - lastFetchAtRef.current < MIN_REFETCH_GAP_MS) {
      return;
    }

    const ctrl = new AbortController();
    const promise = (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/notifications`, {
          method: "GET",
          credentials: "include",
          signal: ctrl.signal,
        });
        const json = await res.json().catch(() => null);

        if (!res.ok || !json?.success) {
          throw new Error(json?.message || `Request failed (${res.status})`);
        }

        if (!mountedRef.current) return;
        setNotifications(Array.isArray(json.data) ? json.data : []);
        setUnreadCount(Number.isFinite(json.unreadCount) ? json.unreadCount : 0);
        lastFetchAtRef.current = Date.now();
      } catch (err) {
        if (err.name === "AbortError") return;
        if (!mountedRef.current) return;
        console.error("[notifications] fetch failed:", err.message);
        setError(err.message);
      } finally {
        if (mountedRef.current) setLoading(false);
        inFlightRef.current = null;
        if (refetchQueuedRef.current && mountedRef.current) {
          refetchQueuedRef.current = false;
          setTimeout(() => fetchNotificationsRef.current?.({ force: true }), 0);
        }
      }
    })();

    promise.cancel = () => ctrl.abort();
    inFlightRef.current = promise;
    return promise;
  }, []);
  const fetchNotificationsRef = useRef(fetchNotifications);
  fetchNotificationsRef.current = fetchNotifications;

  // ---- Lifecycle: initial load + SSE push + focus/visibility safety net ----
  //
  // Re-runs whenever `isLoggedIn` flips (login/logout). On logout the previous
  // cleanup function closes the SSE connection and clears state, so the
  // browser doesn't keep retrying the /stream endpoint with no cookie.
  useEffect(() => {
    mountedRef.current = true;

    // Not logged in (or just logged out) — clear stale state and do nothing.
    // No fetch, no SSE, no event listeners. Endpoint is never called.
    if (!isLoggedIn) {
      setNotifications([]);
      setUnreadCount(0);
      setError(null);
      return () => {
        mountedRef.current = false;
      };
    }

    // 1. Initial hydration — fetch the current list when the provider mounts.
    fetchNotifications({ force: true });

    // 2. SSE: real-time push from backend.
    //    The browser opens a long-running GET to /api/notifications/stream.
    //    Cookies (and the auth middleware) work the same as every other route.
    //    EventSource handles reconnection automatically on disconnect.
    const es = new EventSource(`${API_BASE}/api/notifications/stream`, {
      withCredentials: true,
    });

    es.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg?.type === "notification.new") {
          fetchNotifications({ force: true });
          // A new team invite arrives as a notification; refresh the invite list.
          dispatch(orgApi.util.invalidateTags(["OrgInvites"]));
        } else if (msg?.type === "org.invites_changed") {
          // A team invited this user, or took an invite back: refresh the
          // invite banner and the notification list right away.
          dispatch(orgApi.util.invalidateTags(["OrgInvites"]));
          fetchNotifications({ force: true });
        } else if (msg?.type === "org.changed") {
          // ZeroTyping Teams: someone changed this user's team (removed them,
          // changed their role, accepted an invite…). Refetch the team data so
          // the sidebar, Team page and Manage Subscription update at once.
          syncTeamState();
        }
      } catch {
        /* ignore malformed payloads */
      }
    };

    // EventSource reconnects on its own (server restart, network blip, sleep).
    // Anything sent while it was down is lost, so catch up on every reconnect.
    let opened = false;
    es.onopen = () => {
      if (opened) {
        fetchNotifications({ force: true });
        syncTeamState();
      }
      opened = true;
    };

    es.onerror = () => {
      // EventSource will auto-reconnect — onopen catches up when it does.
    };

    // 3. Safety net: catch up on missed events when the user returns to the
    //    tab. Real-time pushes already handle the happy path; these handlers
    //    cover the case where the stream dropped while disconnected.
    const onFocus = () => fetchNotifications();
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchNotifications();
        // Team state too, in case an org.changed event was missed while away.
        syncTeamState({ force: false });
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      mountedRef.current = false;
      try { es.close(); } catch { /* ignore */ }
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      if (inFlightRef.current?.cancel) inFlightRef.current.cancel();
    };
  }, [isLoggedIn, fetchNotifications, dispatch, syncTeamState]);

  // ---- Optimistic mark-as-read for a single notification ----
  const markAsRead = useCallback(async (id) => {
    let wasUnread = false;
    setNotifications((prev) =>
      prev.map((n) => {
        if (n.id !== id) return n;
        if (!n.read) wasUnread = true;
        return { ...n, read: true };
      })
    );
    if (wasUnread) {
      setUnreadCount((c) => Math.max(0, c - 1));
    }

    try {
      const res = await fetch(`${API_BASE}/api/notifications/${id}/read`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Mark-read failed (${res.status})`);
    } catch (err) {
      console.error("[notifications] markAsRead failed:", err.message);
      // Roll back optimistic update by refetching authoritative state.
      fetchNotifications({ force: true });
    }
  }, [fetchNotifications]);

  // ---- Optimistic mark-all-as-read ----
  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);

    try {
      const res = await fetch(`${API_BASE}/api/notifications/read-all`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Mark-all-read failed (${res.status})`);
    } catch (err) {
      console.error("[notifications] markAllRead failed:", err.message);
      fetchNotifications({ force: true });
    }
  }, [fetchNotifications]);

  const refresh = useCallback(() => fetchNotifications({ force: true }), [fetchNotifications]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      error,
      markAsRead,
      markAllRead,
      refresh,
    }),
    [notifications, unreadCount, loading, error, markAsRead, markAllRead, refresh]
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within a <NotificationsProvider>");
  }
  return ctx;
};
